/**
 * 多容器 / 四 Tab Shell 下 Web 的「路由元数据」：tab 与 containerId。
 * 优先读原生注入（document start），其次读 URL query（便于联调与无注入环境）。
 */

/** 原生 Detail 二级 WebView 注入 / Android URL query 对齐用 */
export const NATIVE_DETAIL_CONTAINER_ID = "native-detail";

export interface ContainerRouteMeta {
  /** 原生 WebView 索引（统一壳：0 主壳 · 1 Chat）；非 App 或未标注时为 null */
  tabIndex: number | null;
  /** 稳定容器标识，如 shell-tab-0、native-detail */
  containerId: string | null;
}

declare global {
  interface Window {
    __EDGEN_TAB_INDEX__?: number;
    __EDGEN_CONTAINER_ID__?: string;
  }
}

function parseIntLoose(raw: string | null): number | null {
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

/** 原生 Chat modal 常驻 WebView 下标（`edgen_tab=1` / `shell-tab-1`） */
const NATIVE_CHAT_SHELL_TAB_INDEX = 1;

/**
 * 当前文档是否运行在原生 Chat 壳 WebView（`shell-tab-1`）内。
 * 仅看容器注入 / query，不含 App 环境判断。
 */
export function isChatShellContainer(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const ctx = getContainerRouteContext();
  return (
    ctx.tabIndex === NATIVE_CHAT_SHELL_TAB_INDEX &&
    ctx.containerId === `shell-tab-${NATIVE_CHAT_SHELL_TAB_INDEX}`
  );
}

/**
 * 当前文档的容器上下文（仅客户端有意义）。
 */
export function getContainerRouteContext(): ContainerRouteMeta {
  if (typeof window === "undefined") {
    return { tabIndex: null, containerId: null };
  }

  let tabIndex: number | null = null;
  if (
    typeof window.__EDGEN_TAB_INDEX__ === "number" &&
    !Number.isNaN(window.__EDGEN_TAB_INDEX__)
  ) {
    tabIndex = window.__EDGEN_TAB_INDEX__;
  } else {
    try {
      tabIndex = parseIntLoose(
        new URLSearchParams(window.location.search).get("edgen_tab"),
      );
    } catch {
      tabIndex = null;
    }
  }

  let containerId: string | null = null;
  if (
    typeof window.__EDGEN_CONTAINER_ID__ === "string" &&
    window.__EDGEN_CONTAINER_ID__.length > 0
  ) {
    containerId = window.__EDGEN_CONTAINER_ID__;
  } else {
    try {
      const q = new URLSearchParams(window.location.search).get(
        "edgen_container",
      );
      if (q) {
        containerId = q;
      }
    } catch {
      containerId = null;
    }
  }

  return { tabIndex, containerId };
}
